/**
 * RepoResetMaintenance - Wartungsbereich im "Space verwalten"-Dialog.
 *
 * Setzt das Pipeline-Repository und den Webapp-Zustand eines Space auf den
 * Auslieferungsstand zurueck. Nur fuer Superadmins bei gebundener Pipeline.
 *
 * Ablauf: Vorschau laden -> Blocker pruefen -> Name bestaetigen -> Ausfuehren -> Ergebnis.
 */
import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Alert, ButtonBase, CircularProgress, Collapse, LinearProgress } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { api } from "../utils/helpers";
import { HomeButton, HomeInput } from "./SpacesHomeControls";

// 10 min for execute (many GitHub API calls), 2 min for preview.
const EXECUTE_TIMEOUT = 600000;
const PREVIEW_TIMEOUT = 120000;

// Max items shown before collapsing behind "mehr anzeigen".
const COLLAPSE_LIMIT = 5;

function CollapsibleList({ items, renderItem, moreLabel }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, COLLAPSE_LIMIT);
  const hidden = items.slice(COLLAPSE_LIMIT);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {visible.map(renderItem)}
      {hidden.length > 0 && (
        <>
          <Collapse in={open} unmountOnExit>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {hidden.map(renderItem)}
            </div>
          </Collapse>
          <ButtonBase
            onClick={() => setOpen(v => !v)}
            sx={{
              color: "var(--iio-brand-ink)", fontSize: 12, fontWeight: 500,
              display: "flex", alignItems: "center", gap: "4px", padding: "2px 0",
              borderRadius: "4px",
            }}
          >
            <ExpandMoreRounded style={{ fontSize: 16, transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}/>
            {moreLabel(hidden.length)}
          </ButtonBase>
        </>
      )}
    </div>
  );
}

// All known error codes from the contract.
const KNOWN_CODES = [
  "REPO_RESET_FORBIDDEN", "REPO_RESET_NOT_CONFIGURED", "REPO_RESET_CONFIRM_MISMATCH",
  "REPO_RESET_STALE", "REPO_RESET_BUSY", "REPO_RESET_BACKUP_FAILED",
  "REPO_RESET_PREVIEW_FAILED", "REPO_RESET_FAILED",
  "REPO_RESET_PUSH_TRIGGER", "REPO_RESET_SHARED_REPO", "REPO_RESET_ACTIVE", "REPO_RESET_INTERRUPTED",
];

// Translates error codes from the contract into user-facing messages.
function errorText(t, error) {
  if (!error) return "";
  const code = error.code || error.body?.code;
  if (code && KNOWN_CODES.includes(code)) return t(`wartung.${code}`);
  return error.message || t("laden.unbekannt");
}

// Translates step names from the backend into user-facing text.
function stepLabel(t, step) {
  const key = `wartung.schritt_${step}`;
  const label = t(key);
  // Falls der Schluessel nicht existiert, gibt i18next den Schluessel selbst zurueck.
  return label === key ? step : label;
}

// Checks whether an error is a timeout, 504 gateway timeout, or network failure
// during execute — all mean the server may still be working.
function isTimeoutOrGateway(error) {
  if (error?.name === "AbortError") return true;
  if (error?.status === 504) return true;
  if (error?.name === "TypeError" && /fetch|network/i.test(error?.message)) return true;
  return false;
}

export default function RepoResetMaintenance({ spaceId, spaceName }) {
  const { t } = useTranslation("spaces");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  const abortRef = useRef(null);

  const loadPreview = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    setResult(null);
    setResultError(null);
    setConfirmName("");
    try {
      const data = await api(`/api/cicd/repo-reset/preview?spaceId=${encodeURIComponent(spaceId)}`, { signal: controller.signal, timeout: PREVIEW_TIMEOUT });
      if (!controller.signal.aborted) setPreview(data);
    } catch (e) {
      if (!controller.signal.aborted) setPreviewError(e);
    } finally {
      if (!controller.signal.aborted) setPreviewLoading(false);
    }
  }, [spaceId]);

  const execute = useCallback(async () => {
    if (!preview || confirmName !== spaceName) return;
    setExecuting(true);
    setResultError(null);
    try {
      const data = await api("/api/cicd/repo-reset/execute", {
        method: "POST",
        timeout: EXECUTE_TIMEOUT,
        body: JSON.stringify({ spaceId, previewDigest: preview.previewDigest, confirm: confirmName }),
      });
      setResult(data);
      setPreview(null);
    } catch (e) {
      // STALE: clear preview automatically so the user can reload.
      if (e.code === "REPO_RESET_STALE") {
        setPreview(null);
        setConfirmName("");
      }
      setResultError(e);
    } finally {
      setExecuting(false);
    }
  }, [preview, confirmName, spaceName, spaceId]);

  // Use the name from the preview (server-resolved, handles mirrors) for confirmation.
  const confirmTarget = preview?.spaceName || spaceName;
  const hasBlockers = preview?.blockers?.length > 0;
  const canExecute = preview && !hasBlockers && confirmName === confirmTarget && !executing;

  // Row style for list items.
  const rowStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "6px 10px", borderRadius: 4,
    background: "var(--iio-bg-1)", border: "1px solid var(--iio-border-subtle)",
    fontSize: 13,
  };
  const monoStyle = { fontFamily: "var(--iio-font-mono)", fontSize: 12 };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "var(--iio-text-2)", marginBottom: 6, marginTop: 16 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Description */}
      <p style={{ fontSize: 13, color: "var(--iio-text-2)", margin: 0, lineHeight: 1.5 }}>
        {t("wartung.beschreibung")}
      </p>

      {/* Single preview button: load initially, refresh when preview or result is shown */}
      {!executing && (
        <HomeButton
          className="iio-btn iio-btn--secondary"
          onClick={loadPreview}
          disabled={previewLoading}
          style={{ alignSelf: "flex-start" }}
        >
          {previewLoading ? (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CircularProgress size={14} color="inherit"/>
              {t("wartung.vorschauLaeuft")}
            </span>
          ) : (preview || result) ? t("wartung.vorschauAktualisieren") : t("wartung.vorschauLaden")}
        </HomeButton>
      )}

      {/* Preview error */}
      {previewError && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: 13 }}>
          {t("wartung.vorschauFehler", { grund: errorText(t, previewError) })}
        </Alert>
      )}

      {/* Preview content */}
      {preview && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Blockers — show translated heading for known codes, plus server message for detail */}
          {hasBlockers && preview.blockers.map((b, i) => {
            const code = b.code;
            const heading = code && KNOWN_CODES.includes(code) ? t(`wartung.${code}`) : null;
            const detail = b.message;
            return (
              <Alert key={i} severity="warning" variant="outlined" sx={{ mb: 1, fontSize: 13 }} data-testid="repo-reset-blocker">
                {heading && detail && heading !== detail ? (
                  <><span style={{ fontWeight: 600 }}>{heading}</span>{" "}{detail}</>
                ) : (heading || detail || t("laden.unbekannt"))}
              </Alert>
            );
          })}

          {/* Repository info */}
          <div style={labelStyle}>{t("wartung.repoTitel")}</div>
          <div style={{ ...rowStyle, flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
            {preview.repo?.htmlUrl ? (
              <a href={preview.repo.htmlUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: "var(--iio-brand-ink)", textDecoration: "none" }}>
                {preview.repo.owner}/{preview.repo.name}
              </a>
            ) : (
              <span style={{ fontSize: 13 }}>{preview.repo?.owner}/{preview.repo?.name}</span>
            )}
            <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>
              {t("wartung.defaultBranch")}: <span style={monoStyle}>{preview.defaultBranch}</span>
              {" · "}
              {t("wartung.contentBranch")}: <span style={monoStyle}>{preview.contentBranch}</span>
            </span>
          </div>

          {/* Branches */}
          <div style={labelStyle}>{t("wartung.branchesTitel")}</div>
          <CollapsibleList
            items={preview.branches}
            moreLabel={n => t("wartung.branchesMehr", { count: n })}
            renderItem={(b, i) => (
              <div key={b.name || i} style={rowStyle}>
                <span style={{ ...monoStyle, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {b.name}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, flexShrink: 0, marginLeft: 12,
                  color: b.action === "delete" ? "var(--iio-critical)" : "var(--iio-text-3)",
                }}>
                  {b.action === "delete" ? t("wartung.branchWirdGeloescht") : t("wartung.branchBleibt")}
                </span>
              </div>
            )}
          />

          {/* Tree entries per branch */}
          {(preview.trees || []).map(tree => (
            <div key={tree.branch}>
              <div style={labelStyle}>{t("wartung.eintraegeTitel", { branch: tree.branch })}</div>
              <CollapsibleList
                items={tree.entries}
                moreLabel={n => t("wartung.eintraegeMehr", { count: n })}
                renderItem={(e, i) => (
                  <div key={e.path || i} style={rowStyle}>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                      {e.path}
                      {e.files != null && (
                        <span style={{ color: "var(--iio-text-3)", fontFamily: "inherit", marginLeft: 6 }}>
                          ({t("wartung.eintraegeDateien", { count: e.files })})
                        </span>
                      )}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, flexShrink: 0, marginLeft: 12,
                      color: e.action === "remove" ? "var(--iio-critical)" : "var(--iio-text-3)",
                    }}>
                      {e.action === "remove" ? t("wartung.eintragWirdEntfernt") : t("wartung.eintragBleibt")}
                    </span>
                  </div>
                )}
              />
            </div>
          ))}

          {/* Webapp state */}
          {preview.webapp?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.webappTitel")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {preview.webapp.map((w, i) => (
                  <div key={w.store || i} style={rowStyle}>
                    <span>{w.label}</span>
                    <span style={{ fontSize: 12, color: "var(--iio-text-2)", fontWeight: 500 }}>
                      {t("wartung.webappEintraege", { count: w.count })}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Running runs */}
          <div style={labelStyle}>{t("wartung.laufendeLaeufe")}</div>
          {(preview.runningRuns?.length || 0) === 0 ? (
            <span style={{ fontSize: 13, color: "var(--iio-text-3)" }}>{t("wartung.keineLaufendenLaeufe")}</span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {preview.runningRuns.map((r, i) => (
                <div key={r.id || i} style={rowStyle}>
                  <span>{r.name}</span>
                  {r.htmlUrl ? (
                    <a href={r.htmlUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: "var(--iio-brand-ink)", textDecoration: "none" }}>
                      #{r.id}
                    </a>
                  ) : <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>#{r.id}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Scheduled workflows */}
          <div style={labelStyle}>{t("wartung.zeitgesteuerteWorkflows")}</div>
          {(preview.scheduledWorkflows?.length || 0) === 0 ? (
            <span style={{ fontSize: 13, color: "var(--iio-text-3)" }}>{t("wartung.keineZeitgesteuertenWorkflows")}</span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {preview.scheduledWorkflows.map((w, i) => (
                <div key={w.path || i} style={rowStyle}>
                  <span>{w.name}</span>
                  <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>
                    {(w.crons || []).join(", ")}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Push-trigger workflows (warning, shown alongside scheduled) */}
          {preview.pushTriggers?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.pushTriggerTitel")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {preview.pushTriggers.map((wf, i) => (
                  <div key={wf.path || i} style={rowStyle}>
                    <span>{wf.name}</span>
                    <span style={{ fontSize: 12, color: "var(--iio-text-3)" }}>
                      {(wf.branches || []).join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Workflow runs to be deleted */}
          <div style={labelStyle}>{t("wartung.workflowLaeufeTitel")}</div>
          {(preview.workflowRuns?.length || 0) === 0 ? (
            <span style={{ fontSize: 13, color: "var(--iio-text-3)" }}>{t("wartung.workflowLaeufeKeine")}</span>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {preview.workflowRuns.map((wf, i) => (
                <div key={wf.name || i} style={rowStyle}>
                  <span>{wf.name}</span>
                  <span style={{ fontSize: 12, color: "var(--iio-text-2)", fontWeight: 500 }}>
                    {t("wartung.workflowLaeufeAnzahl", { count: wf.completed })}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* SAP info: use backend sapNotice if provided, otherwise the fixed hint */}
          <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 0, fontSize: 13 }} data-testid="repo-reset-sap-hint">
            {preview.sapNotice || t("wartung.sapHinweis")}
          </Alert>

          {/* Archive hint */}
          <p style={{ fontSize: 12, color: "var(--iio-text-3)", marginTop: 16, marginBottom: 0, fontStyle: "italic" }}>
            {t("wartung.archivHinweis")}
          </p>

          {/* Confirmation */}
          <div style={{
            marginTop: 20, padding: 12,
            border: "1px solid var(--iio-critical)",
            borderLeft: "3px solid var(--iio-critical)",
            borderRadius: "var(--iio-r-sm)",
            background: "var(--iio-bg-1)",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            <label htmlFor="repo-reset-confirm" style={{ fontSize: 13, color: "var(--iio-text-1)", fontWeight: 500 }}>
              {t("wartung.bestaetigungLabel")}
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <HomeInput
                id="repo-reset-confirm"
                aria-label={t("wartung.bestaetigungLabel")}
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={confirmTarget}
                disabled={executing}
              />
              <HomeButton
                className="iio-btn iio-btn--danger"
                disabled={!canExecute}
                onClick={execute}
                style={{ flexShrink: 0 }}
              >
                {executing ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CircularProgress size={14} color="inherit"/>
                    {t("wartung.ausfuehrenLaeuft")}
                  </span>
                ) : t("wartung.ausfuehren")}
              </HomeButton>
            </div>
          </div>

          {/* Execution progress */}
          {executing && <LinearProgress sx={{ mt: 1, borderRadius: 1 }}/>}
        </div>
      )}

      {/* Execute error — outside preview block so it stays visible after STALE clears preview */}
      {resultError && (
        <Alert severity={isTimeoutOrGateway(resultError) ? "info" : "error"} variant="outlined" sx={{ fontSize: 13 }} data-testid="repo-reset-error">
          {isTimeoutOrGateway(resultError) ? t("wartung.zeitueberschreitung") : errorText(t, resultError)}
        </Alert>
      )}

      {/* Result */}
      {result && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Alert
            severity={result.ok ? "success" : "warning"}
            variant="outlined"
            sx={{ fontSize: 13 }}
            data-testid="repo-reset-result"
          >
            {result.ok ? t("wartung.ergebnisTitel") : t("wartung.ergebnisTeilerfolgTitel")}
          </Alert>

          {/* Backup ID */}
          {result.backupId && (
            <>
              <div style={labelStyle}>{t("wartung.sicherungsId")}</div>
              <div style={{ ...rowStyle, justifyContent: "flex-start" }}>
                <span style={{ fontSize: 13 }}>{result.backupId}</span>
              </div>
            </>
          )}

          {/* Archive tags */}
          {result.tags?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.archivTags")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {result.tags.map((tag, i) => (
                  <div key={tag.tag || i} style={rowStyle}>
                    <span style={monoStyle}>{tag.tag}</span>
                    {tag.ok === false && <span style={{ fontSize: 11, color: "var(--iio-critical)", fontWeight: 600, marginLeft: 8 }}>{tag.error || t("wartung.fehlgeschlagen")}</span>}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Deleted branches */}
          {result.deletedBranches?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.geloeschteBranches")}</div>
              <CollapsibleList
                items={result.deletedBranches}
                moreLabel={n => t("wartung.branchesMehr", { count: n })}
                renderItem={(b, i) => (
                  <div key={b.name || i} style={rowStyle}>
                    <span style={monoStyle}>{b.name}</span>
                    {b.ok === false && b.error && (
                      <span style={{ fontSize: 11, color: "var(--iio-critical)", fontWeight: 600, marginLeft: 8 }}>{b.error}</span>
                    )}
                  </div>
                )}
              />
            </>
          )}

          {/* Commits */}
          {result.commits?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.neueCommits")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {result.commits.map((c, i) => (
                  <div key={c.branch || i} style={rowStyle}>
                    <span style={monoStyle}>{c.branch}</span>
                    <span style={{ ...monoStyle, color: c.skipped ? "var(--iio-text-3)" : "var(--iio-text-1)" }}>
                      {c.skipped ? t("wartung.commitUebersprungen") : c.sha?.slice(0, 8)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Cleared stores */}
          {result.webapp?.length > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.bereinigteSpeicher")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {result.webapp.map((w, i) => (
                  <div key={w.store || i} style={rowStyle}>
                    <span>{w.label}</span>
                    <span style={{ fontSize: 12, color: "var(--iio-text-2)", fontWeight: 500 }}>
                      {t("wartung.speicherEntfernt", { count: w.removed })}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Deleted workflow runs */}
          {result.deletedRuns && result.deletedRuns.total > 0 && (
            <>
              <div style={labelStyle}>{t("wartung.geloeschteLaeufe")}</div>
              <div style={rowStyle}>
                <span>{t("wartung.geloeschteLaeufeDetail", { ok: result.deletedRuns.ok, failed: result.deletedRuns.failed })}</span>
              </div>
            </>
          )}

          {/* Errors — group deleteRun errors into a single summary */}
          {result.errors?.length > 0 && (() => {
            const deleteRunErrors = result.errors.filter(e => e.step === "deleteRun");
            const otherErrors = result.errors.filter(e => e.step !== "deleteRun");
            return (
              <>
                <div style={{ ...labelStyle, color: "var(--iio-critical)" }}>{t("wartung.fehlerImSchritt")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {otherErrors.map((e, i) => (
                    <Alert key={i} severity="error" variant="outlined" sx={{ fontSize: 12 }}>
                      <strong>{stepLabel(t, e.step)}</strong>: {e.message || e.code}
                    </Alert>
                  ))}
                  {deleteRunErrors.length > 0 && (
                    <Alert severity="error" variant="outlined" sx={{ fontSize: 12 }} data-testid="repo-reset-deleterun-errors">
                      <strong>{stepLabel(t, "deleteRun")}</strong>: {t("wartung.deleteRunFehler", { count: deleteRunErrors.length })}
                    </Alert>
                  )}
                </div>
              </>
            );
          })()}

          {/* The single "Vorschau aktualisieren" button at the top serves this purpose. */}
        </div>
      )}
    </div>
  );
}
